import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from './api';
import { Confirm, useToast, type ConfirmSpec } from './ui';

/**
 * Time off (docs/prismhr.md): employees ask for UNPAID time off on their phone; managers approve, deny or
 * approve as sick pay on the Employees page; the weekly "Time off for payroll" list is ticked off by
 * admins, HR or the store manager (level 9) once each item is entered in PrismHR by hand.
 */

export interface TimeOffReq {
  id: number; employeeId: number; who: string; when: string; note: string; status: string; statusText: string;
  kind: string; sick: boolean; hours: number; asked: string; onPayroll: boolean;
}
interface SickRow { source: 'time_off' | 'day_change'; id: number; name: string; when: string; hours: number; how: string; entered: boolean; kind: 'sick' | 'unpaid'; what: string }

function isoAdd(iso: string, n: number) { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function sundayOf(iso: string) { return isoAdd(iso, -new Date(iso + 'T12:00:00Z').getUTCDay()); }
function md(iso: string) { const [, m, d] = iso.split('-'); return `${Number(m)}/${Number(d)}`; }
function todayIso() { return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date()); }
export function tlabel(b: number) {
  const m = 300 + 15 * b; const h = Math.floor(m / 60) % 24; const mm = m % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${mm < 10 ? '0' : ''}${mm}${h >= 12 ? 'p' : 'a'}`;
}

/** The Employees page boxes: "Time-off requests" and "Time off for payroll". */
export function TimeOffBoxes({ locationId }: { locationId: number }) {
  const [reqs, setReqs] = useState<{ requests: TimeOffReq[]; canDecide: boolean; mine?: number | null } | null>(null);
  const [week, setWeek] = useState(() => sundayOf(todayIso()));
  const [sick, setSick] = useState<{ rows: SickRow[]; canTick: boolean } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [toast, showToast] = useToast();
  const load = useCallback(async () => {
    setReqs(await get(`/api/time-off?location=${locationId}`));
  }, [locationId]);
  const loadSick = useCallback(async () => {
    setSick(await get(`/api/time-off/sick-pay?location=${locationId}&week=${week}`));
  }, [locationId, week]);
  useEffect(() => { void load().catch(() => setReqs({ requests: [], canDecide: false })); }, [load]);
  useEffect(() => { void loadSick().catch(() => setSick({ rows: [], canTick: false })); }, [loadSick]);

  const decide = (t: TimeOffReq, status: 'approved' | 'denied', kind?: 'unpaid' | 'sick') => {
    const spec: ConfirmSpec = status === 'denied'
      ? { title: 'Deny time off?', text: `${t.who}’s request for ${t.when} will be marked denied. They’ll see that on their time-off page.`, ok: 'Deny', run: () => go() }
      : kind === 'sick'
        ? { title: 'Approve as sick pay?', text: `Only for illness or medical reasons. ${t.who} asked for ${t.when}, ${t.hours} hrs${t.note ? ` (${t.note})` : ''}. The ${t.hours} h are added to the sick pay list for payroll, and it shows on the day board like other time off.`, ok: 'Approve as sick pay', run: () => go() }
        : { title: 'Approve as unpaid time off?', text: `${t.who} asked for ${t.when}, ${t.hours} hrs${t.note ? ` (${t.note})` : ''}. Approving shows it on the day board, and if they are already scheduled then, their shift is underlined in purple on the day board so you can move it.`, ok: 'Approve', run: () => go() };
    const go = async () => {
      try { await post(`/api/time-off/${t.id}/decide`, { status, kind }); await load(); await loadSick(); }
      catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t save. Try again.'); }
    };
    setConfirm(spec);
  };
  const sickH = sick?.rows.filter((x) => x.kind === 'sick').reduce((n, x) => n + x.hours, 0) ?? 0;
  const unpaidH = sick?.rows.filter((x) => x.kind === 'unpaid').reduce((n, x) => n + x.hours, 0) ?? 0;

  return (
    <>
      <div className="reqbox">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}><b style={{ fontSize: 15 }}>Time-off requests</b>
          <span style={{ fontSize: 13, color: '#4A5561' }}>Employees ask from their own page. Approved time off shows on the day board. “Approve as sick pay” adds it to the payroll list below.</span></div>
        {reqs && reqs.requests.length === 0 && <div style={{ fontSize: 14, color: '#4A5561' }}>No requests.</div>}
        {reqs?.requests.map((t) => (
          <div key={t.id} className="torow">
            <b style={{ minWidth: 130 }}>{t.who}</b><span className="mono">{t.when}</span><span>{t.kind}</span>
            <span style={{ color: '#4A5561' }}>{t.note}</span><span style={{ color: '#8A939C', fontSize: 12 }}>{t.asked}</span>
            <span style={{ flex: '1 1 auto' }} />
            {t.onPayroll && <span className="prismtag ok">On the payroll list</span>}
            <span className={'tostat ' + t.status}>{t.statusText}</span>
            {reqs.canDecide && t.status === 'pending' && t.employeeId === reqs.mine && <span style={{ fontSize: 12.5, color: '#4A5561' }}>Your own request: another manager or an admin answers it</span>}
            {reqs.canDecide && t.status === 'pending' && t.employeeId !== reqs.mine && (
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <button className="tbtn primary" style={{ minHeight: 30 }} onClick={() => decide(t, 'approved', 'unpaid')}>Approve</button>
                <button className="tbtn" style={{ minHeight: 30 }} title="Approve and record it as sick pay (illness or medical only)" onClick={() => decide(t, 'approved', 'sick')}>Approve as sick pay</button>
                <button className="danger" style={{ minHeight: 30 }} onClick={() => decide(t, 'denied')}>Deny</button>
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="reqbox">
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px' }}><b style={{ fontSize: 15 }}>Time off for payroll</b>
          <span style={{ fontSize: 13, color: '#4A5561' }}>Week ending Sat {md(isoAdd(week, 6))}. Sick pay and approved unpaid time off, to enter in PrismHR by hand. Tick each one once it’s entered.</span>
          <span style={{ flex: '1 1 auto' }} />
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn2" style={{ minHeight: 30, minWidth: 30, padding: '2px 8px' }} aria-label="Previous week" onClick={() => setWeek((w) => isoAdd(w, -7))}>‹</button>
            <button className="btn2" style={{ minHeight: 30, minWidth: 30, padding: '2px 8px' }} aria-label="Next week" onClick={() => setWeek((w) => isoAdd(w, 7))}>›</button>
          </span>
        </div>
        {sick && sick.rows.length === 0 && <div style={{ fontSize: 14, color: '#4A5561' }}>No sick pay or unpaid time off this week.</div>}
        {sick?.rows.map((x) => (
          <div key={x.source + x.id} className="torow">
            <b style={{ minWidth: 130 }}>{x.name}</b><span className="mono">{x.when}</span><b>{x.hours} h</b><span className={'paykind ' + x.kind}>{x.what}</span><span style={{ color: '#4A5561' }}>{x.how}</span>
            <span style={{ flex: '1 1 auto' }} />
            <label className="allday"><input type="checkbox" checked={x.entered} disabled={!sick.canTick} aria-label={`Entered in PrismHR: ${x.name} ${x.when}`}
              onChange={async (e) => {
                try { await post('/api/time-off/sick-pay/entered', { source: x.source, id: x.id, on: e.target.checked }); await loadSick(); }
                catch (err) { showToast(err instanceof ApiError ? err.message : 'That didn’t save.'); }
              }} />Entered in PrismHR</label>
          </div>
        ))}
        {sick && sick.rows.length > 0 && <div style={{ fontSize: 13, color: '#17212B', fontWeight: 600 }}>{[sickH ? `${sickH} h sick pay` : '', unpaidH ? `${unpaidH} h unpaid time off` : ''].filter(Boolean).join(' · ')} this week · {sick.rows.filter((x) => !x.entered).length} still to enter</div>}
      </div>
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {toast}
    </>
  );
}

/** The phone "Time off" tab. */
export function TimeOffTab({ store, onChange }: { store: string; onChange: () => void }) {
  const [list, setList] = useState<TimeOffReq[] | null>(null);
  const [f, setF] = useState({ from: '', to: '', allDay: true, fromB: '', toB: '', note: '' });
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [toast, showToast] = useToast();
  const load = useCallback(async () => setList((await get<{ requests: TimeOffReq[] }>('/api/time-off/mine')).requests), []);
  useEffect(() => { void load().catch(() => setList([])); }, [load]);
  const fmt = (iso: string) => { const [y, m, d] = iso.split('-'); return `${Number(m)}/${Number(d)}/${y}`; };
  const off = !f.from || (!!f.to && f.to < f.from) || (!f.allDay && (f.fromB === '' || f.toB === ''));
  const send = () => {
    const to = f.to || f.from;
    setConfirm({
      title: 'Send this time-off request?',
      text: `You’re asking for ${f.allDay ? 'the whole day' : `${tlabel(Number(f.fromB))}–${tlabel(Number(f.toB))}`} ${to === f.from ? `on ${fmt(f.from)}` : `from ${fmt(f.from)} to ${fmt(to)}`} as unpaid time off. Your manager at ${store} will approve or deny it, and you’ll see the answer here.`,
      ok: 'Send request',
      run: async () => {
        try {
          await post('/api/time-off/mine', { from: f.from, to: f.to || null, allDay: f.allDay, fromBlock: f.allDay ? null : Number(f.fromB), toBlock: f.allDay ? null : Number(f.toB), note: f.note || null });
          setF({ from: '', to: '', allDay: true, fromB: '', toB: '', note: '' }); await load(); onChange();
        } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t send. Try again.'); }
      },
    });
  };
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Time off</h1>
      <div className="balline"><span style={{ color: '#4A5561', fontSize: 13 }}>Time off you ask for here is unpaid. If you’re out sick, tell your manager; they’ll record it as sick pay.</span></div>
      <div className="toform">
        <div style={{ fontWeight: 600 }}>Ask for unpaid time off</div>
        <div className="formgrid">
          <label>First day<input type="date" className="inp" value={f.from} min={todayIso()} onChange={(e) => setF({ ...f, from: e.target.value })} /></label>
          <label>Last day (if more than one)<input type="date" className="inp" value={f.to} min={f.from || todayIso()} onChange={(e) => setF({ ...f, to: e.target.value })} /></label>
          <label className="allday" style={{ flexDirection: 'row', alignSelf: 'end', paddingBottom: 10 }}><input type="checkbox" checked={f.allDay} onChange={(e) => setF({ ...f, allDay: e.target.checked })} />All day</label>
        </div>
        {!f.allDay && (
          <div className="formgrid">
            <label>From<select className="sel-sm" value={f.fromB} onChange={(e) => setF({ ...f, fromB: e.target.value, toB: '' })} aria-label="Off from">
              <option value="">From</option>{Array.from({ length: 96 }, (_, b) => <option key={b} value={b}>{tlabel(b)}</option>)}</select></label>
            <label>Until<select className="sel-sm" value={f.toB} onChange={(e) => setF({ ...f, toB: e.target.value })} aria-label="Off until">
              <option value="">Until</option>{Array.from({ length: 96 }, (_, i) => i + 1).filter((b) => f.fromB === '' || b > Number(f.fromB)).map((b) => <option key={b} value={b}>{tlabel(b)}</option>)}</select></label>
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Note for your manager (optional)
          <input className="inp" value={f.note} maxLength={200} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. Doctor appointment" /></label>
        <div><button className="tbtn primary" disabled={off} onClick={send}>Send request</button></div>
      </div>
      <div style={{ fontWeight: 600 }}>My requests</div>
      {list && list.length === 0 && <div style={{ fontSize: 14, color: '#4A5561' }}>No requests yet.</div>}
      {list?.map((t) => (
        <div key={t.id} className="torow">
          <span className="mono">{t.when}</span><span>{t.kind}</span><span style={{ color: '#4A5561' }}>{t.note}</span><span style={{ flex: '1 1 auto' }} />
          <span className={'tostat ' + t.status}>{t.statusText}</span>
          {(t.status === 'pending' || t.status === 'approved') && (
            <button className="btn2" style={{ minHeight: 28, padding: '2px 10px' }} onClick={() => setConfirm({
              title: 'Cancel this request?', text: `Your request for ${t.when} will be withdrawn.`, ok: 'Cancel request',
              run: async () => { try { await post(`/api/time-off/${t.id}/cancel`); await load(); onChange(); } catch (e) { showToast(e instanceof ApiError ? e.message : 'That didn’t work.'); } },
            })}>Cancel</button>
          )}
        </div>
      ))}
      {confirm && <Confirm spec={confirm} onClose={() => setConfirm(null)} />}
      {toast}
    </section>
  );
}
